import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { createAiChat, updateAiChat } from 'src/api';
import { useAuthContext } from 'src/auth/hooks';
import type { AiChat, ErrorResponse } from 'src/types';
import { aiChatKeys } from 'src/utils/query-keys';
import { runWithCallbacks } from 'src/utils/run-with-callbacks';

import { deriveChatTitle, toTranscript, type Conversation } from './transcript-codec';

// ----------------------------------------------------------------------

type SaveChatPayload = {
  chatId: AiChat['id'] | null;
  conversation: Conversation;
  onSaved: (chatId: AiChat['id'] | null) => void;
};

/**
 * Saves the running conversation after every completed turn. Persistence is
 * best-effort by design: history is a convenience, so a failed write is logged
 * and forgotten rather than surfaced as an error in the middle of a chat.
 */
export const useChatPersistence = () => {
  const queryClient = useQueryClient();
  const { platformId } = useAuthContext();

  const saveChat = useCallback(
    async ({ chatId, conversation, onSaved }: SaveChatPayload) => {
      const title = deriveChatTitle(conversation.items);
      // Nothing the user would recognise in a list yet — wait for a real turn.
      if (!title) {
        onSaved(chatId);
        return;
      }

      const transcript = toTranscript(conversation);

      const onSuccess = (chat: AiChat) => {
        queryClient.invalidateQueries({ queryKey: aiChatKeys.all(platformId) });
        onSaved(chat.id);
      };
      const onError = (error: ErrorResponse) => {
        console.warn('[ai-orchestrator] failed to save chat history', error);
        onSaved(chatId);
      };

      await runWithCallbacks(
        () =>
          chatId
            ? updateAiChat({ id: chatId, ...transcript })
            : createAiChat({ title, ...transcript }),
        onSuccess,
        onError
      );
    },
    [queryClient, platformId]
  );

  return useMutation({ mutationFn: saveChat });
};
